import { z } from 'zod';

import { CashDenominationRowsSchema } from './cash-denominations.schema.js';
import { CashOperationIdentityDtoSchema } from './cash-operation-identity.schema.js';
import {
  cashAvailabilityReservationStatusValues,
  cashVaultRemittanceDestinationTypeValues,
  cashVaultRemittanceEventTypeValues,
  cashVaultRemittanceStatusValues,
  type CashRemittanceDestinationDto,
  type CashVaultRemittanceActionRequest,
  type CashAvailabilityReservationStatus,
  type CashVaultRemittanceDestinationType,
  type CashVaultRemittanceDto,
  type CashVaultRemittanceEventType,
  type CashVaultRemittanceStatus,
  type CreateCashVaultRemittanceRequest,
  type GetCashVaultRemittanceRequest,
  type ListCashRemittanceDestinationsRequest,
  type ListCashRemittanceDestinationsResponse,
  type ListCashVaultRemittancesRequest,
  type ListCashVaultRemittancesResponse,
  type UpsertCashRemittanceDestinationRequest,
  type UpdateCashVaultRemittanceRequest,
} from './cash-vault-remittances.dto.js';

const timestampSchema = z.iso.datetime();

export const CashVaultRemittanceStatusSchema: z.ZodType<CashVaultRemittanceStatus> = z.enum(
  cashVaultRemittanceStatusValues,
);
export const CashVaultRemittanceDestinationTypeSchema: z.ZodType<CashVaultRemittanceDestinationType> =
  z.enum(cashVaultRemittanceDestinationTypeValues);
export const CashVaultRemittanceEventTypeSchema: z.ZodType<CashVaultRemittanceEventType> = z.enum(
  cashVaultRemittanceEventTypeValues,
);
export const CashAvailabilityReservationStatusSchema: z.ZodType<CashAvailabilityReservationStatus> =
  z.enum(cashAvailabilityReservationStatusValues);

export const CashRemittanceDestinationDtoSchema: z.ZodType<CashRemittanceDestinationDto> = z.object(
  {
    id: z.uuid(),
    companyId: z.uuid(),
    type: CashVaultRemittanceDestinationTypeSchema,
    code: z.string().trim().min(1).max(100),
    displayName: z.string().trim().min(1).max(200),
    isActive: z.boolean(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  },
);

export const ListCashRemittanceDestinationsRequestSchema: z.ZodType<ListCashRemittanceDestinationsRequest> =
  z.object({ companyId: z.uuid(), activeOnly: z.boolean().optional() }).strict();

export const ListCashRemittanceDestinationsResponseSchema: z.ZodType<ListCashRemittanceDestinationsResponse> =
  z.object({ items: z.array(CashRemittanceDestinationDtoSchema) });

export const UpsertCashRemittanceDestinationRequestSchema: z.ZodType<UpsertCashRemittanceDestinationRequest> =
  z
    .object({
      companyId: z.uuid(),
      operationId: z.uuid(),
      destinationId: z.uuid().optional(),
      type: CashVaultRemittanceDestinationTypeSchema,
      code: z.string().trim().min(1).max(100),
      displayName: z.string().trim().min(1).max(200),
      isActive: z.boolean(),
      metadata: z.record(z.string(), z.unknown()).nullable().optional(),
      occurredAt: timestampSchema.optional(),
    })
    .strict();

export const CashVaultRemittanceDtoSchema: z.ZodType<CashVaultRemittanceDto> =
  CashOperationIdentityDtoSchema.extend({
    id: z.uuid(),
    status: CashVaultRemittanceStatusSchema,
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    sourceVaultCashLocationId: z.uuid(),
    destinationType: CashVaultRemittanceDestinationTypeSchema,
    destinationId: z.uuid(),
    destinationCashLocationIdSnapshot: z.uuid().nullable(),
    amountMinor: z.number().int().positive(),
    denominations: CashDenominationRowsSchema.nullable(),
    reference: z.string().trim().min(1).max(200).nullable(),
    note: z.string().trim().min(1).max(500).nullable(),
    preparedByUserId: z.uuid().nullable(),
    preparedAt: timestampSchema.nullable(),
    handedOffByUserId: z.uuid().nullable(),
    handedOffAt: timestampSchema.nullable(),
    vaultOutMovementId: z.uuid().nullable(),
    destinationMovementId: z.uuid().nullable(),
    returnedVaultInMovementId: z.uuid().nullable(),
    resolutionReason: z.string().trim().min(1).max(500).nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const CreateCashVaultRemittanceRequestSchema: z.ZodType<CreateCashVaultRemittanceRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    sourceVaultCashLocationId: z.uuid(),
    destinationType: CashVaultRemittanceDestinationTypeSchema,
    destinationId: z.uuid(),
    amountMinor: z.number().int().positive(),
    denominations: CashDenominationRowsSchema.nullable().optional(),
    reference: z.string().trim().min(1).max(200).nullable().optional(),
    note: z.string().trim().min(1).max(500).nullable().optional(),
    occurredAt: timestampSchema.optional(),
  })
  .strict();

export const UpdateCashVaultRemittanceRequestSchema: z.ZodType<UpdateCashVaultRemittanceRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    remittanceId: z.uuid(),
    destinationType: CashVaultRemittanceDestinationTypeSchema,
    destinationId: z.uuid(),
    amountMinor: z.number().int().positive(),
    denominations: CashDenominationRowsSchema.nullable().optional(),
    reference: z.string().trim().min(1).max(200).nullable().optional(),
    note: z.string().trim().min(1).max(500).nullable().optional(),
    occurredAt: timestampSchema.optional(),
  })
  .strict();

export const CashVaultRemittanceActionRequestSchema: z.ZodType<CashVaultRemittanceActionRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    remittanceId: z.uuid(),
    reason: z.string().trim().min(1).max(500).nullable().optional(),
    evidenceReference: z.string().trim().min(1).max(200).nullable().optional(),
    actualAmountMinor: z.number().int().positive().optional(),
    actualDenominations: CashDenominationRowsSchema.nullable().optional(),
    occurredAt: timestampSchema.optional(),
  })
  .strict();

export const ListCashVaultRemittancesRequestSchema: z.ZodType<ListCashVaultRemittancesRequest> = z
  .object({
    companyId: z.uuid(),
    locationId: z.uuid().optional(),
    sourceVaultCashLocationId: z.uuid().optional(),
    status: CashVaultRemittanceStatusSchema.optional(),
  })
  .strict();

export const ListCashVaultRemittancesResponseSchema: z.ZodType<ListCashVaultRemittancesResponse> =
  z.object({ items: z.array(CashVaultRemittanceDtoSchema) });

export const GetCashVaultRemittanceRequestSchema: z.ZodType<GetCashVaultRemittanceRequest> = z
  .object({ companyId: z.uuid(), remittanceId: z.uuid(), locationId: z.uuid().optional() })
  .strict();
